import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import AgentKnowledgeManager from './pages/AgentKnowledgeManager';
import SalesRepsManager from './pages/SalesRepsManager';
import {
  LayoutDashboard,
  Flame,
  Users,
  Settings,
  Database,
  LogOut,
  ChevronRight,
  ChevronDown,
  Music,
  ExternalLink,
  Disc3,
  ShieldCheck,
  Film,
  GraduationCap,
  Building,
  Handshake,
  Briefcase,
  MapPin,
  Moon,
  Sun,
  Factory,
  Award,
  Calendar,
  Inbox,
  Bot,
  BadgePercent,
  BookOpen,
  Server
} from 'lucide-react'; // HMR TEST 2


import { AuthGuard, RoleGuard, useAuth } from './components/AuthGuard';
import SpotlightManager from './pages/SpotlightManager';
import Dashboard from './pages/Dashboard';
import ProjectDetails from './pages/ProjectDetails';
import TrackRegistry from './pages/TrackRegistry';
import LabelLanding from './pages/LabelLanding';
import MyPortfolioPage from './pages/MyPortfolioPage';
import FinancialDashboard from './pages/FinancialDashboard';
import GlobalConfig from './pages/GlobalConfig';
import FilmRegistry from './pages/FilmRegistry';
import FilmProjectDetails from './pages/FilmProjectDetails';
import BlankLanding from './pages/BlankLanding';
import StudentManager from './pages/StudentManager';
import TrainerManager from './pages/TrainerManager';
import CampusManager from './pages/CampusManager';
import SponsorManager from './pages/SponsorManager';
import PartnerManager from './pages/PartnerManager';
import ProgramManager from './pages/ProgramManager';
import InstitutionManager from './pages/InstitutionManager';
import IndustryManager from './pages/IndustryManager';
import ScholarshipManager from './pages/ScholarshipManager';
import UniversalRegistry from './pages/UniversalRegistry';
import UniversalBible from './pages/UniversalBible';
import PitchDeckPage from './pages/PitchDeckPage';
import AccessManager from './pages/AccessManager';
import DispatchQueue from './pages/DispatchQueue';
import GlobalCalendar from './pages/GlobalCalendar';
import CourseRegistry from './pages/CourseRegistry';
import TcmManager from './pages/TcmManager';
import OnboardingAccessManager from './pages/OnboardingAccessManager';
import PortalAccessManager from './pages/PortalAccessManager';

export const PartnerContext = React.createContext({
  activePartner: 'global',
  setActivePartner: () => {}
});

const allAdmins = ['ecosystem_director', 'owner', 'manager'];

const PARTNER_OPTIONS = [
  { id: 'global', label: 'Global DIS View' },
  { id: 'teau', label: 'The East African University' },
  { id: 'embu', label: 'County Government of Embu' },
  { id: 'machakos', label: 'County Government of Machakos' },
  { id: 'east_trust', label: 'E.A.S.T. Trust' },
  { id: 'vtc_init', label: 'V.T.C. Initiative' }
];

const SidebarLink = (props) => {
  const { to, icon: IconComponent, label, active } = props;
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
        active
          ? 'bg-[#8B1A2D]/10 text-[#8B1A2D] border-transparent shadow-sm'
          : 'text-zinc-500 dark:text-zinc-400 hover:text-black dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 border border-transparent'
      }`}
    >
      <IconComponent
        size={16}
        className={`shrink-0 ${active ? 'text-[#8B1A2D]' : 'group-hover:text-black dark:group-hover:text-white transition-colors'}`}
      />
      <span className="font-black text-[10px] tracking-wider uppercase truncate">{label}</span>
      {active && <ChevronRight size={12} className="ml-auto text-[#8B1A2D] shrink-0" />}
    </Link>
  );
};

const Sidebar = () => {
  const location = useLocation();
  const { role, logout } = useAuth();
  const { activePartner, setActivePartner } = React.useContext(PartnerContext);
  
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('dis-dark-mode') === 'true';
  });

  const [sidebarWidth, setSidebarWidth] = useState(256);
  const [isResizing, setIsResizing] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    'Registry Core': true,
    'Admissions & Financials': false,
    'System Assets': false
  });

  const startResizing = React.useCallback((e) => {
    setIsResizing(true);
    e.preventDefault();
  }, []);

  const stopResizing = React.useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = React.useCallback((e) => {
    if (isResizing) {
      setSidebarWidth(Math.max(200, Math.min(600, e.clientX)));
    }
  }, [isResizing]);

  useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  const toggleGroup = (label) => {
    setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('dis-dark-mode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('dis-dark-mode', 'false');
    }
  }, [isDarkMode]);

  const isActive = (to, exact = false) => {
    if (exact) return location.pathname === to;
    return location.pathname === to || location.pathname.startsWith(to + '/');
  };

  const isArtistPortal = window.location.hostname === 'portfolio.dynamicindutechno.solutions' || window.location.hostname.includes('portfolio') || window.location.hostname === 'portal.dynamicindutechno.solutions' || window.location.hostname.includes('portal') || window.location.hostname.includes('8081');

  // ROLE-BASED NAVIGATION
  const menuGroups = [
    {
      label: 'Registry Core',
      items: [
        { to: isArtistPortal ? '/dashboard' : '/',  icon: LayoutDashboard, label: 'Dashboard',      exact: true, roles: [...allAdmins, 'viewer', 'artist'] },
        { to: '/universal',  icon: Users,           label: 'Universal Registry', roles: allAdmins },
        { to: '/students',   icon: GraduationCap,   label: 'Student Registry', roles: [...allAdmins, 'viewer'] },
        { to: '/campuses',   icon: MapPin,          label: 'Campus Registry', roles: allAdmins },
        { to: '/institutions',icon: Building,       label: 'Institution Registry', roles: allAdmins },
        { to: '/industries', icon: Factory,         label: 'Industry Registry', roles: allAdmins },
        { to: '/tcm',        icon: Server,          label: 'Tasks & Customer Mgmt (TCM)', roles: allAdmins },
        { to: '/partners',   icon: Handshake,       label: 'Partner Registry', roles: allAdmins },
        { to: '/programs',   icon: BookOpen,        label: 'Program Registry', roles: allAdmins }
      ]
    },
    {
      label: 'Admissions & Financials',
      items: [
        { to: '/onboarding-access', icon: ShieldCheck,   label: 'Onboarding Access (Gate 1)', roles: allAdmins },
        { to: '/portal-access',     icon: GraduationCap, label: 'Portal Access (Gate 2)',     roles: allAdmins },
        { to: '/sponsors',   icon: Briefcase,       label: 'Sponsor Registry', roles: allAdmins },
        { to: '/scholarships',icon: Award,          label: 'Scholarships', roles: allAdmins },
        { to: '/sales-reps',  icon: BadgePercent,   label: 'Sales Rep Codes', roles: allAdmins },
        { to: '/dispatch',    icon: Inbox,          label: 'Dispatch Queue', roles: allAdmins },
        { to: '/calendar',    icon: Calendar,       label: 'Global Calendar', roles: allAdmins }
      ]
    },
    {
      label: 'System Assets',
      items: [
        { to: '/trainers',   icon: ShieldCheck,     label: 'Trainer Management', roles: allAdmins },
        { to: '/pitch-deck', icon: Film, shadow: true, label: 'Nexus Pitch Deck', roles: [...allAdmins, 'viewer', 'artist'] },
        { to: '/ai-knowledge', icon: Bot,          label: 'AI Knowledge Hub',   roles: ['ecosystem_director', 'owner', 'manager'] },
        { to: '/access',     icon: ShieldCheck,     label: 'Access Control',              roles: ['ecosystem_director'] },
        { to: '/settings',  icon: Settings,        label: 'Global Config',               roles: ['ecosystem_director'] }
      ]
    }
  ];

  const getFilteredGroups = () => {
    return menuGroups.map(group => ({
      ...group,
      items: group.items.filter(item => item.roles.includes(role))
    })).filter(group => group.items.length > 0);
  };
  
  const filteredGroups = getFilteredGroups();
  
  const formatRole = (r) => {
      if (!r) return '';
      return r.replace('_', ' ').toUpperCase();
  };

  return (
    <div 
      className="relative min-h-screen border-r border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex flex-col py-8 px-4 sticky top-0 shrink-0 shadow-sm z-10 transition-[width] duration-75 ease-linear overflow-x-hidden"
      style={{ width: `${sidebarWidth}px` }}
    >
      {/* Resizer Handle */}
      <div 
        className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize hover:bg-[#8B1A2D]/20 z-20"
        onMouseDown={startResizing}
      />

      {/* Logo */}
      <div className="mb-6 px-3 shrink-0">
        <div 
          className="mb-2 flex items-center gap-1"
          style={{ 
            fontFamily: "'Plus Jakarta Sans', 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
            fontSize: '0.825rem',
            textTransform: 'none'
          }}
        >
          <span className="text-zinc-500 dark:text-zinc-400" style={{ textTransform: 'none' }}>Powered by</span>
          <span 
            style={{ 
              fontFamily: "'Plus Jakarta Sans', 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              fontWeight: 800,
              color: '#E8BC6A',
              letterSpacing: '0.04em',
              textTransform: 'none'
            }}
          >
            Mô∅D
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white dark:bg-zinc-800 flex items-center justify-center border border-zinc-200 dark:border-zinc-700 shadow-sm shrink-0 overflow-hidden">
            <img src="/dis_logo_crimson.svg" alt="DIS Logo" className="w-6 h-6 object-contain" />
          </div>
          <div className="truncate">
            <h1 className="text-sm font-black tracking-tight text-zinc-900 dark:text-white leading-none truncate">DIS Registry</h1>
            <p className="text-[8px] font-black text-zinc-500 dark:text-zinc-400 uppercase tracking-[0.25em] mt-0.5 truncate">
                {formatRole(role)}
            </p>
          </div>
        </div>
      </div>

      {/* Global Partner Selector */}
      <div className="px-2 mb-8 shrink-0">
        <label className="block text-[9px] font-black tracking-[0.2em] text-zinc-400 uppercase mb-2 ml-2">Active Workspace</label>
        <select
          value={activePartner}
          onChange={(e) => setActivePartner(e.target.value)}
          className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-zinc-900 dark:text-white text-xs font-bold rounded-lg px-3 py-2 outline-none focus:border-[#8B1A2D] transition-colors appearance-none cursor-pointer"
        >
          {PARTNER_OPTIONS.map(opt => (
            <option key={opt.id} value={opt.id}>{opt.label}</option>
          ))}
        </select>
      </div>

      <nav className="flex-1 space-y-4 overflow-y-auto pr-2 pb-4 scrollbar-hide">
        {filteredGroups.map((group, gIdx) => {
          const isExpanded = expandedGroups[group.label];
          return (
            <div key={gIdx} className="space-y-1">
              <button 
                onClick={() => toggleGroup(group.label)}
                className="w-full flex items-center gap-2 px-2 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-800 rounded-lg transition-colors group"
              >
                {isExpanded ? <ChevronDown size={14} className="text-zinc-400 group-hover:text-black dark:group-hover:text-white shrink-0" /> : <ChevronRight size={14} className="text-zinc-400 group-hover:text-black dark:group-hover:text-white shrink-0" />}
                <h3 className="text-[9px] font-black tracking-[0.2em] text-zinc-400 uppercase group-hover:text-black dark:group-hover:text-white truncate">
                  {group.label}
                </h3>
              </button>
              
              {isExpanded && (
                <div className="pl-3 space-y-1 border-l border-zinc-100 dark:border-zinc-800 ml-3.5 mt-1">
                  {group.items.map((item) => (
                    <SidebarLink
                      key={item.to}
                      {...item}
                      active={isActive(item.to, item.exact)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="mt-auto space-y-2 pt-6 border-t border-zinc-100 dark:border-zinc-800 shrink-0">
        <a
          href="https://alumni.dynamicindutechno.solutions"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-zinc-500 dark:text-zinc-400 hover:text-black dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all text-[10px] font-black uppercase tracking-widest truncate"
        >
          <ExternalLink size={13} className="shrink-0" /> <span className="truncate">Live Directory</span>
        </a>

        <button 
          onClick={() => setIsDarkMode(!isDarkMode)}
          className="flex items-center justify-between px-3 py-2.5 text-zinc-500 dark:text-zinc-400 hover:text-black dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all w-full rounded-xl"
        >
          <div className="flex items-center gap-3 truncate">
            {isDarkMode ? <Sun size={15} className="shrink-0 text-amber-500" /> : <Moon size={15} className="shrink-0" />}
            <span className="font-black text-[10px] uppercase tracking-widest truncate">
              {isDarkMode ? 'Light Mode' : 'Dark Mode'}
            </span>
          </div>
        </button>

        <button 
          onClick={logout}
          className="flex items-center gap-3 px-3 py-2.5 text-zinc-500 dark:text-zinc-400 hover:text-[#8B1A2D] transition-colors w-full group rounded-xl hover:bg-[#8B1A2D]/5"
        >
          <LogOut size={15} className="shrink-0" />
          <span className="font-black text-[10px] uppercase tracking-widest truncate">Sign Out</span>
        </button>
      </div>
    </div>
  );
};

// ── Placeholder page factory ────────────────────────────────────────────
const PlaceholderPage = ({ label }) => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <p className="text-zinc-300 font-black italic text-5xl uppercase tracking-tighter">{label}</p>
  </div>
);

export default function App() {
  const isArtistPortal = window.location.hostname === 'portfolio.dynamicindutechno.solutions' || window.location.hostname.includes('portfolio') || window.location.hostname === 'portal.dynamicindutechno.solutions' || window.location.hostname.includes('portal') || window.location.hostname.includes('8081');
  const [showDemo, setShowDemo] = useState(false);
  const [activePartner, setActivePartner] = useState('global');

  return (
    <PartnerContext.Provider value={{ activePartner, setActivePartner }}>
      <Router>
        <Routes>
          {/* PUBLIC ROUTES (No AuthGuard) */}
          <Route path="/" element={<BlankLanding />} />
          {isArtistPortal && (
            <Route path="/portfolio-preview" element={<LabelLanding />} />
          )}


          {/* SECURE ROUTES (AuthGuard) */}
          <Route path="/*" element={
            <AuthGuard>
              <div className="flex bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 h-screen overflow-hidden font-['Montserrat']">
                <Sidebar />
                <main className="flex-1 h-full overflow-y-auto custom-scrollbar relative">
                  <div className="max-w-6xl mx-auto p-10 pb-32">
                    <Routes>
                      <Route path="/"               element={isArtistPortal ? <MyPortfolioPage /> : <Dashboard />} />
                      <Route path="/dashboard"      element={isArtistPortal ? <MyPortfolioPage /> : <Dashboard />} />
                      <Route path="/financials"     element={<FinancialDashboard />} />
                      <Route path="/sales-reps"     element={<RoleGuard allowedRoles={['ecosystem_director', 'owner', 'manager']}><SalesRepsManager /></RoleGuard>} />
                      <Route path="/ai-knowledge font" element={<RoleGuard allowedRoles={['ecosystem_director', 'owner', 'manager']}><AgentKnowledgeManager /></RoleGuard>} />
                      <Route path="/ai-knowledge"   element={<RoleGuard allowedRoles={['ecosystem_director', 'owner', 'manager']}><AgentKnowledgeManager /></RoleGuard>} />
                      <Route path="/access"         element={<RoleGuard allowedRoles={['ecosystem_director']}><AccessManager /></RoleGuard>} />
                      <Route path="/settings"       element={<RoleGuard allowedRoles={['ecosystem_director']}><GlobalConfig /></RoleGuard>} />
                      <Route path="/pitch-deck"     element={<PitchDeckPage />} />
                      
                      {/* The God View */}
                      <Route path="/universal"      element={<UniversalRegistry showDemo={showDemo} setShowDemo={setShowDemo} />} />
                      
                      <Route path="/students"       element={<StudentManager showDemo={showDemo} setShowDemo={setShowDemo} />} />
                      <Route path="/students/:id"   element={<UniversalBible collectionName="students" title="Student Profile" />} />
                      
                      {/* Calendar & Dispatch */}
                      <Route path="/dispatch"       element={<DispatchQueue />} />
                      <Route path="/calendar"       element={<GlobalCalendar />} />
                      <Route path="/onboarding-access" element={<RoleGuard allowedRoles={[...allAdmins, 'viewer']}><OnboardingAccessManager /></RoleGuard>} />
                      <Route path="/portal-access"     element={<RoleGuard allowedRoles={[...allAdmins, 'viewer']}><PortalAccessManager /></RoleGuard>} />
                      
                      <Route path="/trainers"       element={<TrainerManager showDemo={showDemo} setShowDemo={setShowDemo} />} />
                      <Route path="/trainers/:id"   element={<UniversalBible collectionName="trainers" title="Trainer Profile" />} />
                      
                      <Route path="/campuses"       element={<CampusManager showDemo={showDemo} setShowDemo={setShowDemo} />} />
                      <Route path="/campuses/:id"   element={<UniversalBible collectionName="campuses" title="Campus Profile" />} />
                      
                      <Route path="/institutions"   element={<RoleGuard allowedRoles={['coordinator']}><InstitutionManager showDemo={showDemo} setShowDemo={setShowDemo} /></RoleGuard>} />
                      <Route path="/institutions/:id" element={<RoleGuard allowedRoles={['coordinator']}><UniversalBible collectionName="institutions" title="Learning Institution Profile" /></RoleGuard>} />
                      
                      <Route path="/industries"     element={<RoleGuard allowedRoles={['coordinator']}><IndustryManager showDemo={showDemo} setShowDemo={setShowDemo} /></RoleGuard>} />
                      <Route path="/industries/:id" element={<RoleGuard allowedRoles={['coordinator']}><UniversalBible collectionName="industries" title="Industry Employer Profile" /></RoleGuard>} />
                      
                      <Route path="/tcm"            element={<RoleGuard allowedRoles={['ecosystem_director', 'owner', 'manager']}><TcmManager showDemo={showDemo} setShowDemo={setShowDemo} /></RoleGuard>} />
                      
                      <Route path="/partners"       element={<RoleGuard allowedRoles={['ecosystem_director', 'owner', 'manager']}><PartnerManager showDemo={showDemo} setShowDemo={setShowDemo} /></RoleGuard>} />
                      <Route path="/partners/:id"   element={<RoleGuard allowedRoles={['ecosystem_director', 'owner', 'manager']}><UniversalBible collectionName="partners" title="Partner Profile" /></RoleGuard>} />
                      
                      <Route path="/programs"       element={<RoleGuard allowedRoles={['ecosystem_director', 'owner', 'manager']}><ProgramManager showDemo={showDemo} setShowDemo={setShowDemo} /></RoleGuard>} />
                      
                      <Route path="/sponsors"       element={<RoleGuard allowedRoles={['viewer']}><SponsorManager showDemo={showDemo} setShowDemo={setShowDemo} /></RoleGuard>} />
                      <Route path="/sponsors/:id"   element={<RoleGuard allowedRoles={['viewer']}><UniversalBible collectionName="sponsors" title="Sponsor Profile" /></RoleGuard>} />
                      
                      <Route path="/scholarships"   element={<RoleGuard allowedRoles={['viewer']}><ScholarshipManager showDemo={showDemo} setShowDemo={setShowDemo} /></RoleGuard>} />
                      <Route path="/scholarships/:id" element={<RoleGuard allowedRoles={['viewer']}><UniversalBible collectionName="scholarships" title="Scholarship Profile" /></RoleGuard>} />
                      <Route path="/courses"        element={<RoleGuard allowedRoles={['ecosystem_director', 'owner', 'manager']}><CourseRegistry /></RoleGuard>} />
                    </Routes>
                  </div>
                </main>
              </div>
            </AuthGuard>
          } />
        </Routes>
      </Router>
    </PartnerContext.Provider>
  );
}
